'use client';
import React from 'react';
import { IconFilter } from '@repo/svgs';
import { SearchInputPill } from '@/components/ui/SearchInputPill';
import { useStore } from '@/providers/StoreProvider';
import LocationFilterMobile from '@/features/listings/filters/location/LocationFilter.Mobile';
import { FiltersBarMobile } from './FiltersBar.Mobile';
import { Badge, BottomSheet } from '@/components/ui';
import { AllFilters } from '@/features/listings/filters/AllFilters';
import { FilterActionBar } from '@/features/listings/filters/FilterActionBar';
import { cn } from '@/lib/cn';
import { useActiveFiltersCount } from '@/features/listings/filters/hooks/useActiveFiltersCount';
import { LogoLink } from '@/components/shared/LogoLink';
import { useAnalytics } from '@/hooks/useAnalytics';

export function ListingsMobileHeader() {
  const analytics = useAnalytics();
  const selectionLabel = useStore(s => s.listingsFilters.getLocationSelectionLabel());
  const resetNeighborhoods = useStore(s => s.listingsFilters.resetNeighborhoods);
  const resetBedrooms = useStore(s => s.listingsFilters.resetBedrooms);
  const resetPrice = useStore(s => s.listingsFilters.resetPrice);
  const resetAllFilters = useStore(s => s.listingsFilters.resetAllFilters);
  const setBuildingStatusFilter = useStore(s => s.listingsFilters.setBuildingStatusFilter);
  const fetchNeighborhoods = useStore(s => s.neighborhoods.fetchNeighborhoods);
  const getUrlFilterPayload = useStore(s => s.listingsFilters.getUrlFilterPayload);
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const [isLocationSheetOpen, setIsLocationSheetOpen] = React.useState(false);
  const [isAllFiltersSheetOpen, setIsAllFiltersSheetOpen] = React.useState(false);

  const { activeFilterCount, hasActiveFilters } = useActiveFiltersCount();
  React.useEffect(() => {
    void fetchNeighborhoods();
  }, [fetchNeighborhoods]);

  const handleClearLocation = () => {
    analytics.track('listings_filter_neighborhood_reset');
    resetNeighborhoods();
    resetBuilding();
    const url = getUrlFilterPayload();
    window.history.replaceState(null, '', url ? `/listings/${url}` : '/listings');
  };

  const openLocationSheet = React.useCallback(() => {
    analytics.track('listings_filter_neighborhood_open');
    setIsLocationSheetOpen(true);
  }, [analytics]);

  return (
    <div>
      <div className="flex items-center gap-4 px-4 py-4 w-full">
        <LogoLink className="shrink-0" />
        <div className="flex flex-1 items-center gap-3 min-w-0">
          <SearchInputPill
            className="flex-1 min-w-0"
            placeholder="Search neighborhood"
            value={selectionLabel ?? 'All of NYC'}
            showClearButton={!!selectionLabel}
            onClear={handleClearLocation}
            onClick={openLocationSheet}
            readOnly={true}
            onFocus={openLocationSheet}
          />
          <div className="relative shrink-0">
            {activeFilterCount > 0 && (
              <Badge count={activeFilterCount} className="!absolute -top-1 -right-0.5" />
            )}
            <button
              type="button"
              aria-label="Reset filters"
              onClick={() => {
                analytics.track('listings_filter_all_open');
                setIsAllFiltersSheetOpen(true);
              }}
              className={cn(
                // Size & layout
                'inline-flex p-3 items-center justify-center rounded-full',
                // Appearance
                ' text-secondary',
                // Interaction
                'transition hover:opacity-80',
                hasActiveFilters && 'bg-active ring-accent ring-2',
              )}
            >
              <IconFilter
                className={cn(hasActiveFilters ? 'icon-accent' : 'icon-secondary')}
                width={16}
                height={16}
              />
            </button>
          </div>
        </div>
      </div>
      <FiltersBarMobile />
      <LocationFilterMobile
        open={isLocationSheetOpen}
        requireConfirmation={true}
        onClose={() => {
          analytics.track('listings_filter_neighborhood_close');
          setIsLocationSheetOpen(false);
        }}
      />
      <BottomSheet
        label="All filters"
        leading={false}
        open={isAllFiltersSheetOpen}
        onClose={() => {
          analytics.track('listings_filter_all_close');
          setIsAllFiltersSheetOpen(false);
        }}
        footer={
          <FilterActionBar
            scope="all"
            onApply={() => {
              analytics.track('listings_filter_all_seeresults');
              setIsAllFiltersSheetOpen(false);
            }}
            onReset={() => {
              analytics.track('listings_filter_all_reset');
              resetAllFilters();
              setBuildingStatusFilter('isVerified', false);
            }}
          />
        }
      >
        <AllFilters />
      </BottomSheet>
    </div>
  );
}

export default ListingsMobileHeader;
